import { effect, Injector, signal } from '@angular/core';
import type { Account, Category } from '@app/domain/entities';
import type { ISettingsRepository } from '@app/domain/ports';
import { PumbImportConfig } from './pumb-import.models';
import { PUMB_SETTINGS } from './pumb-setting-keys';

export class PumbSettingsService {
  readonly pumbEnabled = signal(false);

  constructor(
    private readonly settings: ISettingsRepository,
    injector: Injector,
  ) {
    this.pumbEnabled.set(this.settings.get<boolean>(PUMB_SETTINGS.IMPORT_ENABLED, false));

    effect(
      () => {
        this.settings.set(PUMB_SETTINGS.IMPORT_ENABLED, this.pumbEnabled());
      },
      { injector },
    );
  }

  enablePumb(): void {
    this.pumbEnabled.set(true); // Effect handles persistence
  }

  disablePumb(): void {
    this.pumbEnabled.set(false); // Effect handles persistence
  }

  getImportConfig(): PumbImportConfig | undefined {
    return this.settings.get<PumbImportConfig>(PUMB_SETTINGS.IMPORT_CONFIG);
  }

  saveImportConfig(config: PumbImportConfig): void {
    this.settings.set(PUMB_SETTINGS.IMPORT_CONFIG, config);
  }

  normalizeImportConfig(
    config: PumbImportConfig | undefined,
    accounts: readonly Account[],
    categories: readonly Category[],
  ): PumbImportConfig | undefined {
    if (!config) {
      return undefined;
    }

    const visibleAccountIds = new Set(accounts.filter((account) => !account.hidden).map((account) => account.id));
    const visibleCategories = new Map(
      categories.filter((category) => !category.hidden).map((category) => [category.id, category] as const),
    );

    const normalized: PumbImportConfig = {};

    if (config.accountId !== undefined && visibleAccountIds.has(config.accountId)) {
      normalized.accountId = config.accountId;
    }

    if (config.expenseCategoryId !== undefined && visibleCategories.has(config.expenseCategoryId)) {
      normalized.expenseCategoryId = config.expenseCategoryId;
    }

    if (
      normalized.expenseCategoryId !== undefined &&
      this.isValidChildCategory(normalized.expenseCategoryId, config.expenseSubcategoryId, visibleCategories)
    ) {
      normalized.expenseSubcategoryId = config.expenseSubcategoryId;
    }

    if (config.incomeCategoryId !== undefined && visibleCategories.has(config.incomeCategoryId)) {
      normalized.incomeCategoryId = config.incomeCategoryId;
    }

    if (
      normalized.incomeCategoryId !== undefined &&
      this.isValidChildCategory(normalized.incomeCategoryId, config.incomeSubcategoryId, visibleCategories)
    ) {
      normalized.incomeSubcategoryId = config.incomeSubcategoryId;
    }

    if (typeof config.allowReimportRecords === 'boolean') {
      normalized.allowReimportRecords = config.allowReimportRecords;
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private isValidChildCategory(
    parentId: number | undefined,
    childId: number | undefined,
    categories: ReadonlyMap<number, Category>,
  ): boolean {
    if (parentId === undefined || childId === undefined) {
      return false;
    }

    const child = categories.get(childId);
    return child?.parentId === parentId;
  }
}
